// src/pages/health/WorkoutDayDetail.js
import { useMemo, useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";
import AuthCard from "../../../components/auth/AuthCard";
import BottomNav from "../../../components/common/BottomNav";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import {
    useDayWorkouts,
    useDeleteWorkout,
    useDayWorkoutsInvalidate,
} from "../../../hooks/useWorkouts";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/24/solid";
import WorkoutEditorModal from "./components/WorkoutEditorModal";

// ===== 운동 이미지 import =====
import treadmillIcon from "../../../assets/images/treadmill.png";
import cycle from "../../../assets/images/cycle.png";
import stepper from "../../../assets/images/stepper.png";
import legextension from "../../../assets/images/legextension.png";
import seatedlegpress from "../../../assets/images/seatedlegpress.png";
import seatedlegcurl from "../../../assets/images/seatedlegcurl.png";
import seatedchestpress from "../../../assets/images/seatedchestpress .png";
import butterfly from "../../../assets/images/butterfly.png";
import inclinepress from "../../../assets/images/inclinepress.png";
import latpulldown from "../../../assets/images/latpulldown.png";
import longpull from "../../../assets/images/longpull .png";
import reardeltfly from "../../../assets/images/reardeltfly.png";
import shoulderpress from "../../../assets/images/shoulderpress.png";
import armcurl from "../../../assets/images/armcurl.png";
import dipping from "../../../assets/images/dipping.png";
import abdominal from "../../../assets/images/abdominal.png";
import rotarytorso from "../../../assets/images/rotarytorso.png";
import inneroutthigh from "../../../assets/images/inneroutthigh.png";

import WorkoutDeleteModal from "./components/WorkoutDeleteModal";

/* ───────── 포맷터 ───────── */
const fmt = {
    km: (v) => (v == null ? null : `${Number(v).toFixed(2)}km`),
    min: (v) => (v == null ? null : `${v}분`),
    kph: (v) => (v == null ? null : `${v}km/h`),
    kcal: (v) => (v == null ? null : `${v}kcal`),
    kg: (v) => (v == null ? null : `${v}kg`),
    watt: (v) => (v == null ? null : `${v}W`),
};

const MACHINE_KEY_TO_KO = {
    "leg extension": "레그익스텐션",
    "seated leg press": "시티드 레그프레스",
    "seated leg curl": "시티드 레그컬",
    "seated chest press": "시티드 체스트프레스",
    "butterfly": "버터플라이",
    "incline press": "인클라인 프레스",
    "lat pulldown": "랫풀다운",
    "long pull": "롱풀",
    "rear delt fly": "리어 델트플라이",
    "shoulder press": "숄더프레스",
    "arm curl": "암컬",
    "dipping": "디핑",
    "abdominal": "업도미널",
    "rotary torso": "로타리 토르소",
    "inner outer thigh": "이너/아웃싸이",
};

// 근력/머신 타이틀 한글 변환용
function getMachineDisplayTitle(w = {}) {
    // 1) 우선 title을 machine_key라고 보고 매핑 시도
    const rawTitle = (w.title ?? "").trim();
    if (rawTitle && MACHINE_KEY_TO_KO[rawTitle]) {
        return MACHINE_KEY_TO_KO[rawTitle];
    }

    // 2) 혹시 별도의 machine_key 필드가 있다면 그것도 매핑
    const mKey = (w.machine_key ?? "").trim();
    if (mKey && MACHINE_KEY_TO_KO[mKey]) {
        return MACHINE_KEY_TO_KO[mKey];
    }

    // 3) 이미 한글이 들어온 경우 그대로 사용
    if (rawTitle) return rawTitle;

    return "운동";
}


/* ───────── 유산소/머신 판별 ───────── */
function isCardio(w = {}) {
    const t = String(w.title ?? "").toLowerCase();
    if (String(w.kind ?? "").toLowerCase() === "cardio") return true;
    if (["treadmill", "cycle", "stepper"].includes(String(w.category ?? "").toLowerCase()))
        return true;
    return (
        t.includes("러닝머신") || t.includes("러닝") || t.includes("treadmill") ||
        t.includes("자전거") || t.includes("사이클") || t.includes("cycle") ||
        t.includes("스텝머신") || t.includes("스텝") || t.includes("stepper") || t.includes("계단")
    );
}

// 유산소 3종 이름 정규화
function cardioName(w = {}) {
    const t = String(w.title ?? "").toLowerCase();
    if (t.includes("러닝머신") || t.includes("러닝") || t.includes("treadmill")) return "러닝머신";
    if (t.includes("자전거") || t.includes("사이클") || t.includes("cycle")) return "자전거";
    if (t.includes("스텝머신") || t.includes("스텝") || t.includes("stepper")) return "스텝머신";
    const c = String(w.category ?? "").toLowerCase();
    if (c === "treadmill") return "러닝머신";
    if (c === "cycle") return "자전거";
    if (c === "stepper") return "스텝머신";
    return "러닝머신";
}

/* ───────── 아이콘/필드 메타 ───────── */
const MACHINE_FIELDS = [
    ["소모 칼로리", (w) => fmt.kcal(w?.calories_kcal)],
];

const EXERCISE_META = {
    // 유산소
    "러닝머신": {
        icon: treadmillIcon,
        fields: [
            ["운동속도", (w) => fmt.kph(w?.cardio?.speed_kph ?? w?.speed_kph)],
            ["시간", (w) => fmt.min(w?.duration_min)],
            ["거리", (w) => fmt.km(w?.cardio?.distance_km ?? w?.distance_km)],
        ],
    },
    "자전거": {
        icon: cycle,
        fields: [
            ["watt", (w) => fmt.watt(w?.cardio?.watt_w ?? w?.watt_w)],
            ["시간", (w) => fmt.min(w?.duration_min)],
        ],
    },
    "스텝머신": {
        icon: stepper,
        fields: [
            ["watt", (w) => fmt.watt(w?.cardio?.watt_w ?? w?.watt_w)],
            ["시간", (w) => fmt.min(w?.duration_min)],
        ],
    },
    // 근력머신 15종
    "레그익스텐션": { icon: legextension, fields: MACHINE_FIELDS },
    "시티드 레그프레스": { icon: seatedlegpress, fields: MACHINE_FIELDS },
    "시티드 레그컬": { icon: seatedlegcurl, fields: MACHINE_FIELDS },
    "시티드 체스트프레스": { icon: seatedchestpress, fields: MACHINE_FIELDS },
    "버터플라이": { icon: butterfly, fields: MACHINE_FIELDS },
    "인클라인 프레스": { icon: inclinepress, fields: MACHINE_FIELDS },
    "랫풀다운": { icon: latpulldown, fields: MACHINE_FIELDS },
    "롱풀": { icon: longpull, fields: MACHINE_FIELDS },
    "리어 델트플라이": { icon: reardeltfly, fields: MACHINE_FIELDS },
    "숄더프레스": { icon: shoulderpress, fields: MACHINE_FIELDS },
    "암컬": { icon: armcurl, fields: MACHINE_FIELDS },
    "디핑": { icon: dipping, fields: MACHINE_FIELDS },
    "업도미널": { icon: abdominal, fields: MACHINE_FIELDS },
    "로타리 토르소": { icon: rotarytorso, fields: MACHINE_FIELDS },
    "이너/아웃싸이": { icon: inneroutthigh, fields: MACHINE_FIELDS },
    // 기타 머신 (기본값)
    "머신": {
        icon: legextension,
        fields: MACHINE_FIELDS,
    },
};

function pickMeta(w) {
    if (isCardio(w)) return EXERCISE_META[cardioName(w)];
    const titleKo = getMachineDisplayTitle(w);
    return EXERCISE_META[titleKo] ?? EXERCISE_META["머신"];
}

/* ───────── 카드에 뿌릴 라인 생성 ───────── */
function metricLines(w) {
    const meta = pickMeta(w);
    return meta.fields
        .map(([label, getter]) => {
            const val = getter(w);
            return val ? [label, val] : null;
        })
        .filter(Boolean);
}

/* ───────── 개별 운동 아이템 컴포넌트 (세트 펼치기 로직 분리) ───────── */
function WorkoutItem({ w, editMode, openEdit, requestDelete }) {
    const meta = pickMeta(w);
    const isCardioType = isCardio(w);
    const displayTitle = isCardioType ? cardioName(w) : getMachineDisplayTitle(w);

    // 세트 펼치기 상태 관리
    const [isExpanded, setIsExpanded] = useState(false);

    // 세트 데이터 준비
    const sets = Array.isArray(w.sets) ? w.sets : [];
    const totalSets = sets.length;
    const shouldCollapse = !isCardioType && totalSets > 4;

    // 보여줄 세트 목록 (펼쳐져있으면 전체, 아니면 4개만)
    const visibleSets = shouldCollapse && !isExpanded ? sets.slice(0, 4) : sets;
    const hiddenCount = totalSets - visibleSets.length;

    return (
        <article
            className={`relative rounded-xl border border-gray-200 p-4 transition shadow-sm
            ${editMode ? "bg-white" : "hover:bg-gray-50 cursor-pointer"}`}
        // onClick={!editMode ? () => openEdit(w) : undefined}
        >
            <div className="grid grid-cols-[30%_65%] gap-4 items-start">
                {/* 좌측 아이콘 영역 */}
                <div className="rounded-full flex items-center justify-center self-center">
                    <img
                        src={meta.icon}
                        alt={`${displayTitle} 아이콘`}
                        className="w-20 h-20 md:w-24 md:h-24 object-contain"
                    />
                </div>

                {/* 오른쪽 정보 영역 */}
                <div className="space-y-2 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-x-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <h4 className="font-bold text-lg md:text-xl truncate text-gray-800">
                                {displayTitle}
                            </h4>
                            {/* [변경] 총 세트 표시: 근력운동인 경우 타이틀 옆에 배지 형태로 표시 */}
                            {!isCardioType && totalSets > 0 && (
                                <span className="shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                    {totalSets}세트
                                </span>
                            )}
                        </div>

                        {editMode && (
                            <div className="flex items-center shrink-0 gap-1 ml-auto">
                                <button
                                    aria-label="편집"
                                    onClick={(e) => { e.stopPropagation(); openEdit(w); }}
                                    className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-500 transition-colors"
                                >
                                    <PencilSquareIcon className="w-5 h-5" />
                                </button>
                                <button
                                    aria-label="삭제"
                                    onClick={(e) => { e.stopPropagation(); requestDelete(w); }}
                                    className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-red-500 transition-colors"
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>

                    <hr className="border-gray-100" />

                    {/* 상세 정보 영역 */}
                    {isCardioType ? (
                        // 유산소 운동: 요약 정보 표시
                        (() => {
                            const metrics = metricLines(w);
                            return (
                                <dl className="grid grid-cols-2 gap-y-1 pt-1 text-sm">
                                    {metrics.length > 0
                                        ? metrics.flatMap(([k, v], i) => ([
                                            <dt key={`k-${i}`} className="text-gray-500 font-medium">{k}</dt>,
                                            <dd key={`v-${i}`} className="text-left text-gray-700">{v}</dd>
                                        ]))
                                        : <div className="col-span-2 text-gray-400 text-sm">상세 정보 없음</div>}
                                </dl>
                            );
                        })()
                    ) : (
                        // 근력/머신 운동: 세트 테이블
                        <div className="pt-1">
                            {totalSets > 0 ? (
                                <>
                                    <table className="w-full table-fixed text-sm border-separate border-spacing-y-1">
                                        <colgroup>
                                            <col className="w-[15%]" />
                                            <col className="w-[55%]" />
                                            <col className="w-[30%]" />
                                        </colgroup>
                                        <thead className="text-xs text-gray-400 font-normal">
                                            <tr>
                                                <th className="text-center pb-1">SET</th>
                                                <th className="text-center pb-1">무게</th>
                                                <th className="text-center pb-1">횟수</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-700">
                                            {visibleSets.map((set, index) => (
                                                <tr key={index} className="rounded-sm">
                                                    <td className="text-center py-1 rounded-l-md text-xs text-gray-500">
                                                        {index + 1}
                                                    </td>
                                                    <td className="text-center py-1 font-medium">
                                                        {fmt.kg(set.weight_kg) || "-"}
                                                    </td>
                                                    <td className="text-center py-1 rounded-r-md">
                                                        {set.reps != null ? `${set.reps}회` : "-"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* [변경] 세트 펼치기/접기 버튼 */}
                                    {shouldCollapse && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsExpanded(!isExpanded);
                                            }}
                                            className="w-full mt-2 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        >
                                            {isExpanded ? (
                                                <>
                                                    <span>접기</span>
                                                    <ChevronUpIcon className="w-3 h-3" />
                                                </>
                                            ) : (
                                                <>
                                                    <span>{hiddenCount}개 세트 더 보기</span>
                                                    <ChevronDownIcon className="w-3 h-3" />
                                                </>
                                            )}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="text-gray-400 text-sm py-2">기록된 세트가 없습니다</div>
                            )}

                            {/* 추가 정보 (칼로리 등) */}
                            <div className="text-right text-xs text-gray-400 mt-2">
                                {metricLines(w).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}


/* ───────── 메인 컴포넌트 ───────── */
export default function WorkoutDayDetail() {
    const ready = useLoadingGate();
    const [sp] = useSearchParams();

    // 드롭다운 외부 클릭 닫기
    const [isCatOpen, setIsCatOpen] = useState(false);
    const catRef = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (catRef.current && !catRef.current.contains(e.target)) setIsCatOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const dateStr = sp.get("date") || format(new Date(), "yyyy-MM-dd");
    const dateObj = useMemo(() => {
        const d = parseISO(dateStr);
        return isValid(d) ? d : new Date();
    }, [dateStr]);

    const { data: workouts = [], isLoading } = useDayWorkouts(dateObj);

    // 분류 (전체 / 유산소운동 / 근력운동) 고정
    const [cat, setCat] = useState("전체");
    const categories = useMemo(() => ["전체", "유산소운동", "근력운동"], []);

    const filtered = useMemo(() => {
        if (cat === "전체") return workouts;
        if (cat === "유산소운동") return workouts.filter((w) => isCardio(w));
        return workouts.filter((w) => !isCardio(w)); // 근력운동
    }, [workouts, cat]);

    // 수정 토글 모드
    const [editMode, setEditMode] = useState(false);

    // 모달 상태
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorMode, setEditorMode] = useState("create");
    const [editing, setEditing] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const openCreate = () => {
        setEditing(null);
        setEditorMode("create");
        setEditorOpen(true);
    };
    const openEdit = (w) => {
        setEditing(w);
        setEditorMode("edit");
        setEditorOpen(true);
    };

    // 삭제 훅
    const deleteMut = useDeleteWorkout();
    const invalidate = useDayWorkoutsInvalidate();
    const requestDelete = (w) => {
        if (!w) return;
        setDeleteTarget({ ...w, date: dateStr });
        setDeleteOpen(true);
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title={format(dateObj, "yyyy년 M월 d일")}>
                    <div className="flex flex-col flex-1 pt-3 pb-24">
                        {/* 상단: 제목/버튼 */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-baseline gap-3">
                                <div className="text-xl font-bold">운동 내역</div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="h-9 px-3 rounded-full border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-colors text-sm"
                                    onClick={openCreate}
                                >
                                    추가
                                </button>
                                <button
                                    className={`h-9 px-3 rounded-full border transition text-sm font-medium
                                    ${editMode
                                            ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-600"
                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
                                    onClick={() => setEditMode((v) => !v)}
                                >
                                    {editMode ? "완료" : "수정"}
                                </button>
                            </div>
                        </div>

                        <div className="mb-3" ref={catRef}>
                            <button
                                type="button"
                                onClick={() => setIsCatOpen((v) => !v)}
                                aria-haspopup="listbox"
                                aria-expanded={isCatOpen}
                                className="w-full h-10 px-4 rounded-lg border border-gray-200 bg-gray-50 text-gray-800
                                flex items-center justify-between shadow-sm active:scale-[0.99] transition"
                            >
                                <span className="font-medium truncate">{cat}</span>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-500 transition-transform ${isCatOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {isCatOpen && (
                                <div role="listbox" className="relative">
                                    <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg animate-fadeIn">
                                        {categories.map((c) => (
                                            <li key={c} role="option" aria-selected={cat === c}>
                                                <button
                                                    onClick={() => {
                                                        setCat(c);
                                                        setIsCatOpen(false);
                                                    }}
                                                    className={`w-full h-10 px-4 text-left transition
                                                    ${cat === c ? "bg-blue-50 text-blue-600 font-semibold" : "hover:bg-gray-50 text-gray-700"}`}
                                                >
                                                    {c}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* 리스트 */}
                        <section className="mt-2 space-y-4">
                            {isLoading ? (
                                <div className="text-center text-gray-400 py-12 text-sm">불러오는 중…</div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center text-gray-400 py-12 text-sm">아직 기록이 없어요</div>
                            ) : (
                                filtered.map((w) => (
                                    <WorkoutItem
                                        key={w.id}
                                        w={w}
                                        editMode={editMode}
                                        openEdit={openEdit}
                                        requestDelete={requestDelete}
                                    />
                                ))
                            )}
                        </section>
                    </div>
                </AuthCard>
            )}

            {editorOpen && (
                <WorkoutEditorModal
                    open={editorOpen}
                    mode={editorMode}
                    date={dateStr}
                    initial={editing}
                    onClose={() => setEditorOpen(false)}
                />
            )}
            <WorkoutDeleteModal
                open={deleteOpen}
                workout={deleteTarget}
                onClose={() => setDeleteOpen(false)}
                isDeleting={deleteMut.isPending}
                onDelete={async () => {
                    if (!deleteTarget?.id) return;
                    try {
                        await deleteMut.mutateAsync(deleteTarget.id);
                        await invalidate(dateStr);
                        setDeleteOpen(false);
                        setDeleteTarget(null);
                    } catch (e) {
                        console.error(e);
                        alert("삭제 중 오류가 발생했어요.");
                    }
                }}
            />
            <BottomNav className="h-16" />
        </main>
    );
}